import {
  addDoc,
  collection,
  updateDoc,
  doc,
  QueryConstraint,
  query,
  DocumentData,
  QuerySnapshot,
  writeBatch,
  getDocs,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore'
import { getDb } from './firebase.service'
import { appendWithCreatedAt } from '../utils/form.utils'
import { getUsersByIds } from './user.service'
import { BaseStatus } from '../types/base.types'
import { UserRole } from '../types/userRoles'
import { Dayjs } from 'dayjs'
import { StripeStatus } from '../features/Payments/components/PaymentForm/model/model'

/**
 * Submits a new document to a specified Firestore collection.
 *
 * This function appends user information and a timestamp to the data before
 * submitting it to the Firestore database.
 *
 * @template T - The type of the data object to be submitted.
 * @param {T} data - The data object to be submitted.
 * @param {string} collectionName - The name of the Firestore collection.
 * @returns {Promise<string>} A promise that resolves with the document ID.
 * @throws {Error} Throws an error if the submission fails.
 */
export const writeDocument = async <T extends object>(data: T, collectionName: string): Promise<string> => {
  const dataToSubmit = appendWithCreatedAt(data)
  try {
    const db = await getDb()
    const collectionRef = collection(db, collectionName)
    const docRef = await addDoc(collectionRef, dataToSubmit)
    return docRef.id
  } catch (error) {
    console.error(`Error adding document to ${collectionName}:`, error)
    throw new Error('Failed to submit document')
  }
}

/**
 * Updates an existing document in a specified Firestore collection.
 *
 * @template T - The type of the data object to be updated.
 * @param {string} id - The ID of the document to be updated.
 * @param {T} data - The data object containing updated fields.
 * @param {string} collection - The name of the Firestore collection.
 * @returns {Promise<void>} A promise that resolves when the document is successfully updated.
 * @throws {Error} Throws an error if the update fails.
 * @example
 * await updateDocument('123', { status: 'approved' }, 'collectionName')
 */
export const updateDocument = async <T extends object>(id: string, data: T, collection: string): Promise<void> => {
  try {
    const db = await getDb()
    const documentRef = doc(db, collection, id)
    await updateDoc(documentRef, data)
  } catch (error) {
    console.error(`Error updating document with ID: ${id} in collection: ${collection}`, error)
    throw new Error('Failed to update document')
  }
}

/**
 * Fetches documents from a specified Firestore collection and enhances them with user details.
 *
 * @param {string} collectionName - The name of the Firestore collection.
 * @param {QueryConstraint[]} [constraints] - Optional query constraints.
 * @returns {Promise<Array<T & { user: UserDetails | null }>>} A promise that resolves to an array of documents with user details.
 */
export const getDocsAndEnhanceWithUserDetails = async <T extends { userId: string }>(collectionName: string, constraints?: QueryConstraint[]) => {
  const firestoreQuery = await createFirestoreQuery(collectionName, constraints)
  const snapshot = await getDocs(firestoreQuery)
  const documents = mapSnapshotToDocuments<T>(snapshot)
  return enhanceDocumentsWithUserDetails(documents)
}

/**
 * Creates a Firestore query with optional filters and sorting by createdAt in desc order.
 *
 * @param {string} collectionName - The name of the Firestore collection.
 * @param {QueryConstraint[]} [constraints] - Optional filters to apply to the query.
 * @param selectedFields
 * @returns {Query<DocumentData>} A Firestore query object.
 */
export const createFirestoreQuery = async (collectionName: string, constraints?: QueryConstraint[], selectedFields?: string[]) => {
  const db = await getDb()
  const collectionRef = collection(db, collectionName)

  let firestoreQuery = query(collectionRef, ...(constraints || []))

  if (selectedFields && selectedFields.length > 0) {
    firestoreQuery = firestoreQuery.withConverter({
      toFirestore: (data: DocumentData) => data,
      fromFirestore: (snapshot) => {
        const data = snapshot.data()
        const filteredData = selectedFields.reduce((acc, field) => {
          if (field in data) acc[field] = data[field]
          return acc
        }, {} as DocumentData)
        return filteredData
      },
    })
  }

  return firestoreQuery
}

/**
 * Maps a Firestore snapshot to an array of documents.
 *
 * @template T - The type of the document data.
 * @param {QuerySnapshot<DocumentData>} snapshot - The Firestore snapshot.
 * @returns {Array<T>} An array of documents with their data.
 */
export const mapSnapshotToDocuments = <T>(snapshot: QuerySnapshot<DocumentData>) => {
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as T),
  }))
}

/**
 * Combines documents with user details by fetching user information from Firestore.
 *
 * @template T - The type of the document data, which must include a userId field.
 * @param {Array<T>} documents - An array of documents to be combined with user details.
 * @returns {Promise<Array<T & { user: UserDetails | null }>>} A promise that resolves to an array of documents with user details.
 */
export const enhanceDocumentsWithUserDetails = async <T extends { userId: string }>(documents: T[]) => {
  // Filter out undefined userIds
  const userIds = Array.from(new Set(documents.map((doc) => doc.userId).filter((userId) => userId !== undefined)))
  if (userIds.length === 0) return documents

  const userMap = await getUsersByIds(userIds)

  return documents.map((doc) => ({
    ...doc,
    user: doc.userId !== undefined ? userMap[doc.userId] : null, // If userId is undefined, set user to null
  }))
}

/**
 * Writes multiple documents to a specified Firestore collection in a batch.
 *
 * @template T - The type of the data objects to be written.
 * @param {Array<T>} documents - An array of data objects to be written.
 * @param {string} collectionName - The name of the Firestore collection.
 * @returns {Promise<void>} A promise that resolves when the batch write is complete.
 * @throws {Error} Throws an error if the batch write fails.
 */
export const writeMultipleDocuments = async <T extends object>(documents: T[], collectionName: string) => {
  const db = await getDb()
  const batch = writeBatch(db)
  documents.forEach((document) => {
    const docRef = doc(collection(db, collectionName))
    batch.set(docRef, document)
  })
  try {
    await batch.commit()
    console.log(`Batch write to collection: ${collectionName} completed successfully.`)
  } catch (error) {
    console.error(`Error writing batch to collection: ${collectionName}`, error)
    throw new Error('Failed to write multiple documents')
  }
}

/**
 * Creates an order by constraint for the startDate field.
 *
 * @param {'asc' | 'desc'} order - The order direction (ascending or descending).
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const orderByStartDate = (order: 'asc' | 'desc') => orderBy('startDate', order)

/**
 * Creates an order by constraint for the createdAt field.
 *
 * @param {'asc' | 'desc'} order - The order direction (ascending or descending).
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const orderByCreatedAt = (order: 'asc' | 'desc') => orderBy('createdAt', order)

/**
 * Creates an order by constraint for the bookingDate field.
 *
 * @param {'asc' | 'desc'} order - The order direction (ascending or descending).
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const orderByBookingDate = (order: 'asc' | 'desc') => orderBy('bookingDate', order)

/**
 * Creates a where constraint for pending approvals for a specific authority group.
 *
 * @param {role} userAuthority - The authority group of the user.
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const whereApprovalPending = (userAuthority: UserRole) => where(`approvals.${userAuthority}`, '==', BaseStatus.PENDING)

/**
 * Creates a where constraint for a specific date.
 *
 * @param {Dayjs} selectedDate - The selected date.
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const whereBookingDateEqual = (selectedDate: Dayjs) => where('bookingDate', '==', Timestamp.fromDate(selectedDate.toDate()))

/**
 * Creates where constraints for a date range.
 *
 * @param {Dayjs} startDate - The start date of the range.
 * @param {Dayjs} endDate - The end date of the range.
 * @returns {QueryConstraint[]} An array of Firestore query constraints.
 */
export const whereBookingDateRange = (startDate: Dayjs, endDate: Dayjs): QueryConstraint[] => {
  return [where('bookingDate', '>=', Timestamp.fromDate(startDate.toDate())), where('bookingDate', '<=', Timestamp.fromDate(endDate.toDate()))]
}

/**
 * Creates a where constraint for bookings on or after a date.
 *
 * @param {Dayjs} startDate - The earliest booking date (inclusive).
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const whereBookingDateFrom = (startDate: Dayjs) => where('bookingDate', '>=', Timestamp.fromDate(startDate.toDate()))

/**
 * Creates a where constraint for a specific booking ID.
 *
 * @param {}
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const whereBookingStatus = (status: StripeStatus) => where('paymentStatus', '==', status)

/**
 * Creates a where constraint for pending status.
 *
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const whereStatusPending = () => where('status', '==', BaseStatus.PENDING)

/**
 * Creates a where constraint for non-pending status.
 *
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const whereStatusNotPending = () => where('status', 'in', [BaseStatus.APPROVED, BaseStatus.REJECTED])

/**
 * Creates a where constraint for a specific user ID.
 *
 * @param {string} userId - The user ID.
 * @returns {QueryConstraint} A Firestore query constraint.
 */
export const whereUserId = (userId: string) => where('userId', '==', userId)
